import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { FiX } from "react-icons/fi";
import getApiUrl from "../../../config/api";

// Firestore users with no Firebase Auth account, each marked Keep (the
// default) or Delete. Nothing is deleted until the admin confirms; kept users
// show up again at the next check.
function MissingAuthUsersModal({
  users,
  language,
  currentUser,
  onClose,
  onDeleted,
}) {
  const zh = language === "zh";
  const [toDelete, setToDelete] = useState(() => new Set());
  const [confirming, setConfirming] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Escape" && !busy) onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose, busy]);

  const choose = (uid, remove) => {
    setConfirming(false);
    setToDelete((prev) => {
      const next = new Set(prev);
      if (remove) next.add(uid);
      else next.delete(uid);
      return next;
    });
  };

  const deleteChosen = async () => {
    setBusy(true);
    setError("");
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/admin/delete-users-missing-from-auth`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({ uids: [...toDelete] }),
        },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.deleted) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      const deleted = data.deleted;
      setToDelete(
        (prev) => new Set([...prev].filter((uid) => !deleted.includes(uid))),
      );
      if (data.failed?.length) {
        setError(data.failed.map((f) => `${f.uid}: ${f.error}`).join("; "));
      }
      setConfirming(false);
      onDeleted(deleted);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const count = toDelete.size;
  const th =
    "sticky top-0 z-10 px-4 py-2.5 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600";
  const td =
    "px-4 py-2.5 border-b border-gray-100 dark:border-gray-700 text-sm";
  const none = <span className="text-gray-400">—</span>;
  const choice = (active, danger) =>
    `px-3 py-1.5 ${
      active
        ? danger
          ? "bg-red-600 text-white"
          : "bg-gray-100 text-gray-900 dark:bg-gray-600 dark:text-white"
        : "bg-white text-gray-500 hover:bg-gray-50 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
    } disabled:opacity-50`;

  return createPortal(
    <div
      className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-fadeIn"
      onClick={() => !busy && onClose()}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="missing-auth-users-title"
        className="w-full max-w-4xl max-h-[85vh] flex flex-col bg-white dark:bg-gray-800 rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 animate-scaleIn"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-gray-700">
          <div>
            <h2
              id="missing-auth-users-title"
              className="text-lg font-bold text-gray-900 dark:text-white"
            >
              {zh ? "不在 Firebase 認證中的用戶" : "Users not in Firebase Auth"}
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {zh
                ? "這些 Firestore 用戶沒有 Firebase 認證帳號，無法登入。請選擇要刪除的用戶，其餘將保留。"
                : "These Firestore users have no Firebase Auth account, so they can't sign in. Choose which to delete; the rest are kept."}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={busy}
            className="p-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700 disabled:opacity-50"
            aria-label={zh ? "關閉" : "Close"}
          >
            <FiX className="w-5 h-5" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto overscroll-contain">
          <table className="min-w-full border-separate border-spacing-0">
            <thead>
              <tr>
                <th className={th}>{zh ? "電子郵件" : "Email"}</th>
                <th className={th}>{zh ? "姓名" : "Name"}</th>
                <th className={th}>ID</th>
                <th className={th}>{zh ? "群組" : "Group"}</th>
                <th className={th}>{zh ? "處理" : "Action"}</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => {
                const marked = toDelete.has(user.uid);
                return (
                  <tr
                    key={user.uid}
                    className={marked ? "bg-red-50 dark:bg-red-900/20" : ""}
                  >
                    <td
                      className={`${td} text-gray-900 dark:text-white break-all`}
                    >
                      {user.email || none}
                    </td>
                    <td className={`${td} text-gray-600 dark:text-gray-300`}>
                      {user.displayName || none}
                    </td>
                    <td
                      className={`${td} font-mono text-xs text-gray-600 dark:text-gray-300 break-all`}
                    >
                      {user.uid}
                    </td>
                    <td className={td}>
                      {user.groupId ? (
                        <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300 whitespace-nowrap">
                          {user.groupId}
                        </span>
                      ) : (
                        none
                      )}
                    </td>
                    <td className={td}>
                      <div className="inline-flex rounded-lg border border-gray-200 dark:border-gray-600 overflow-hidden text-xs font-medium">
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => choose(user.uid, false)}
                          className={choice(!marked, false)}
                        >
                          {zh ? "保留" : "Keep"}
                        </button>
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => choose(user.uid, true)}
                          className={choice(marked, true)}
                        >
                          {zh ? "刪除" : "Delete"}
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-col gap-2 px-5 py-4 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/60 rounded-b-2xl">
          {error && (
            <p className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {error}
            </p>
          )}
          {confirming && (
            <p className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 px-3 py-2 text-sm text-red-700 dark:text-red-300">
              {zh
                ? `將永久刪除 ${count} 筆用戶資料，並從其群組中移除。這些用戶沒有登入帳號，因此不會影響任何人的登入。此操作無法復原。`
                : `This permanently deletes ${count} user record(s) and removes them from their groups. They have no login, so no one loses access. This can't be undone.`}
            </p>
          )}
          <div className="flex items-center justify-between gap-3">
            <span className="text-sm text-gray-600 dark:text-gray-300">
              {zh
                ? `${users.length} 位中已選 ${count} 位刪除`
                : `${count} of ${users.length} marked for deletion`}
            </span>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={confirming ? () => setConfirming(false) : onClose}
                disabled={busy}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50"
              >
                {confirming ? (zh ? "返回" : "Back") : zh ? "關閉" : "Close"}
              </button>
              <button
                type="button"
                onClick={confirming ? deleteChosen : () => setConfirming(true)}
                disabled={busy || count === 0}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {busy && (
                  <span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" />
                )}
                {confirming
                  ? zh
                    ? `確認刪除 ${count} 位`
                    : `Yes, delete ${count}`
                  : zh
                    ? `刪除 ${count} 位用戶`
                    : `Delete ${count} user${count === 1 ? "" : "s"}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function UserSyncPanel({
  language,
  currentUser,
  onSyncComplete,
  hasRole,
}) {
  const [syncing, setSyncing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  // null until checked; then the Firestore users with no Auth account
  const [missingUsers, setMissingUsers] = useState(null);
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState("");
  const [showMissing, setShowMissing] = useState(false);

  // Opens the review modal when any are found.
  const checkMissingUsers = async () => {
    setChecking(true);
    setCheckError("");
    try {
      const token = await currentUser.getIdToken();
      const response = await fetch(
        `${getApiUrl()}/api/admin/users-missing-from-auth`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const data = await response.json().catch(() => ({}));
      if (!response.ok || !data.success) {
        throw new Error(data.error || `HTTP ${response.status}`);
      }
      setMissingUsers(data.users);
      if (data.users.length > 0) setShowMissing(true);
    } catch (error) {
      setCheckError(error.message);
    } finally {
      setChecking(false);
    }
  };

  const handleDeleted = (deleted) => {
    const remaining = (missingUsers || []).filter(
      (user) => !deleted.includes(user.uid),
    );
    setMissingUsers(remaining);
    if (remaining.length === 0) setShowMissing(false);
    // Reloads the user list, groups and audit log
    if (deleted.length > 0 && onSyncComplete) onSyncComplete();
  };

  const syncAllUsers = async () => {
    setSyncing(true);
    setSyncResult(null);

    try {
      const token = await currentUser.getIdToken();
      // console.log(
      //     "Attempting to sync users with token:",
      //     token.substring(0, 20) + "..."
      // );

      const response = await fetch(`${getApiUrl()}/api/admin/sync-all-users`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      // console.log("Response status:", response.status);
      // console.log("Response headers:", response.headers);

      // Check if response is actually JSON
      const contentType = response.headers.get("content-type");
      if (!contentType || !contentType.includes("application/json")) {
        const textResponse = await response.text();
        console.error(
          "Non-JSON response received:",
          textResponse.substring(0, 200),
        );
        alert(
          `Server error: Expected JSON but received ${contentType}. Check console for details.`,
        );
        return;
      }

      const data = await response.json();

      if (!response.ok) {
        console.error("HTTP error:", response.status, data);
        alert(
          `HTTP Error ${response.status}: ${data.error || "Unknown error"}`,
        );
        return;
      }

      if (data.success) {
        setSyncResult(data.result);
        if (onSyncComplete) {
          onSyncComplete();
        }
        // console.log("Sync completed successfully:", data.result);
        alert(
          `Sync completed: ${data.result.syncedCount} users synced successfully.`,
        );
        // Syncing only copies Auth -> Firestore; leftovers are offered for deletion.
        checkMissingUsers();
      } else {
        console.error("Sync failed with error:", data.error);
        alert(`Sync failed: ${data.error}`);
      }
    } catch (error) {
      console.error("Sync failed:", error);
      alert(`Sync failed: ${error.message}. Check console for details.`);
    } finally {
      setSyncing(false);
    }
  };

  if (hasRole("users.sync") === false) {
    return (
      <div className="text-center flex flex-col items-center p-4 font-semibold">
        <p className="text-gray-600">
          {language === "zh"
            ? "您沒有權限存取此部分。"
            : "You do not have permission to access this section."}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <h2 className="text-2xl font-semibold text-gray-900">
        {language === "en" ? "User Synchronization" : "用戶同步"}
      </h2>
      {/* Sync Controls */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {language === "en"
            ? "Firebase Auth → Firestore Sync"
            : "Firebase 認證 → Firestore 同步"}
        </h3>

        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-blue-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-700">
                {language === "en"
                  ? "This will copy all Firebase Auth users to Firestore for enhanced querying and data management."
                  : "這將把所有 Firebase 認證用戶複製到 Firestore，以便進行增強的查詢和數據管理。"}
              </p>
            </div>
          </div>
        </div>

        <button
          onClick={syncAllUsers}
          disabled={syncing}
          className={`px-6 py-3 rounded-lg font-medium ${
            syncing
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-blue-600 text-white hover:bg-blue-700"
          }`}
        >
          {syncing ? (
            <span className="flex items-center gap-2">
              <span className="flex gap-1">
                <span className="w-2 h-2 bg-orange-300 rounded-full animate-bounce"></span>
                <span
                  className="w-2 h-2 bg-orange-300 rounded-full animate-bounce"
                  style={{ animationDelay: "0.1s" }}
                ></span>
                <span
                  className="w-2 h-2 bg-orange-300 rounded-full animate-bounce"
                  style={{ animationDelay: "0.2s" }}
                ></span>
              </span>
              <span className="text-orange-300">
                {language === "en" ? "Syncing..." : "同步中..."}
              </span>
            </span>
          ) : language === "en" ? (
            "Sync All Users"
          ) : (
            "同步所有用戶"
          )}
        </button>
      </div>

      {/* Firestore users with no Firebase Auth account */}
      <div className="bg-white rounded-lg border p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-2">
          {language === "en"
            ? "Users not in Firebase Auth"
            : "不在 Firebase 認證中的用戶"}
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          {language === "en"
            ? "Firestore users with no Firebase Auth account, left by deleted accounts or by tests. They can't sign in and syncing doesn't remove them. Sync All Users also checks for them."
            : "沒有 Firebase 認證帳號的 Firestore 用戶，通常來自已刪除的帳號或測試。他們無法登入，同步也不會移除他們。「同步所有用戶」完成後也會自動檢查。"}
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <button
            type="button"
            onClick={checkMissingUsers}
            disabled={checking || syncing}
            className="px-4 py-2 rounded-lg text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 disabled:text-gray-500 disabled:cursor-not-allowed"
          >
            {checking
              ? language === "en"
                ? "Checking..."
                : "檢查中..."
              : language === "en"
                ? "Check"
                : "檢查"}
          </button>
          {missingUsers &&
            !checking &&
            !checkError &&
            (missingUsers.length === 0 ? (
              <span className="text-sm text-green-700">
                {language === "en"
                  ? "None found: every Firestore user has a Firebase Auth account."
                  : "沒有找到：所有 Firestore 用戶都有 Firebase 認證帳號。"}
              </span>
            ) : (
              <button
                type="button"
                onClick={() => setShowMissing(true)}
                className="text-sm font-medium text-blue-600 hover:underline"
              >
                {language === "en"
                  ? `${missingUsers.length} found, review`
                  : `找到 ${missingUsers.length} 位，檢視`}
              </button>
            ))}
          {checkError && (
            <span className="text-sm text-red-600">
              {language === "en" ? "Check failed: " : "檢查失敗："}
              {checkError}
            </span>
          )}
        </div>
      </div>

      {showMissing && missingUsers?.length > 0 && (
        <MissingAuthUsersModal
          users={missingUsers}
          language={language}
          currentUser={currentUser}
          onClose={() => setShowMissing(false)}
          onDeleted={handleDeleted}
        />
      )}

      {/* Sync Results */}
      {syncResult && (
        <div className="bg-white rounded-lg border p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {language === "en" ? "Sync Results" : "同步結果"}
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-green-600">
                  {syncResult.syncedCount}
                </p>
                <p className="text-sm text-green-700">
                  {language === "en" ? "Synced Successfully" : "同步成功"}
                </p>
              </div>
            </div>

            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-red-600">
                  {syncResult.failedCount}
                </p>
                <p className="text-sm text-red-700">
                  {language === "en" ? "Failed" : "失敗"}
                </p>
              </div>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="text-center">
                <p className="text-2xl font-bold text-blue-600">
                  {syncResult.totalUsers}
                </p>
                <p className="text-sm text-blue-700">
                  {language === "en" ? "Total Processed" : "總處理數"}
                </p>
              </div>
            </div>
          </div>

          {syncResult.failedUsers && syncResult.failedUsers.length > 0 && (
            <div className="mt-4">
              <h4 className="font-medium text-red-600 mb-2">
                {language === "en" ? "Failed Users:" : "失敗的用戶："}
              </h4>
              <div className="space-y-1">
                {syncResult.failedUsers.map((user, index) => (
                  <p key={index} className="text-sm text-red-600">
                    • {user.email}: {user.error}
                  </p>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
